import type { SiteName } from '@/scopes/common/domain/types'
import { GroupBookingPaymentVariant, GroupBookingPaymentVariantValues } from '@/scopes/payment/domain/groups/types'

const PAYMENT_URLS: Partial<Record<SiteName, string>> = {
  sunwebnl: '/mijn-sunweb/boeking-details/betaling',
  sunwebdk: '/mit-sunweb/booking-detaljer/betaling',
  sunwebfr: '/mon-sunweb/details-reservation/paiement',
  sunwebse: '/mitt-sunweb/boknings-detaljer/betalning',
  sunwebbenl: '/nl/mijn-sunweb/boeking-details/betaling',
  sunwebbefr: '/fr/mon-sunweb/details-reservation/paiement',
  sunwebuk: '/my/booking-details/payment',
  sunwebde: '/mein-sunweb/buchungs-details/zahlung',
  elizanl: '/mijn-eliza/boeking-details/betaling',
  elizabe: '/mijn-eliza/boeking-details/betaling',
}

const GROUP_PAYMENT_URLS: Partial<Record<SiteName, Partial<Record<GroupBookingPaymentVariant, string>>>> = {
  sunwebnl: {
    totallysnow: '/mijn-sunweb/boeking-details/groups/totallysnow/betaling',
    winter: '/mijn-sunweb/boeking-details/groups/winter/betaling',
  },
  sunwebdk: {
    totallysnow: '/mit-sunweb/booking-detaljer/groups/totallysnow/betaling',
  },
  sunwebfr: {
    totallysnow: '/mon-sunweb/details-reservation/groups/totallysnow/paiement',
    winter: '/mon-sunweb/details-reservation/groups/winter/paiement',
  },
  sunwebbenl: {
    totallysnow: '/nl/mijn-sunweb/boeking-details/groups/totallysnow/betaling',
    winter: '/nl/mijn-sunweb/boeking-details/groups/winter/betaling',
  },
  sunwebbefr: {
    totallysnow: '/fr/mon-sunweb/details-reservation/groups/totallysnow/betaling',
    winter: '/fr/mon-sunweb/details-reservation/groups/ski/paiement',
  },
}

const GROUP_PATH_SEGMENT_VARIANTS: Record<string, GroupBookingPaymentVariant> = {
  totallysnow: GroupBookingPaymentVariantValues.TOTALLY_SNOW,
  winter: GroupBookingPaymentVariantValues.WINTER,
  ski: GroupBookingPaymentVariantValues.WINTER,
}

function parsePaymentUrl(sourcePaymentUrl: string): URL | undefined {
  try {
    return new URL(sourcePaymentUrl, window.location.origin)
  }
  catch {
    return undefined
  }
}

function getGroupPaymentVariant(sourceUrl: URL): GroupBookingPaymentVariant | undefined {
  const pathSegments = sourceUrl.pathname.split('/').filter(Boolean).map(segment => segment.toLowerCase())
  const groupsSegmentIndex = pathSegments.lastIndexOf('groups')
  if (groupsSegmentIndex === -1) {
    return undefined
  }

  return GROUP_PATH_SEGMENT_VARIANTS[pathSegments[groupsSegmentIndex + 1]]
}

export function buildPaymentUrl(
  siteName: SiteName,
  bookingNumber: string,
): string | undefined {
  const paymentBaseUrl = PAYMENT_URLS[siteName]
  if (!paymentBaseUrl) {
    return undefined
  }

  const params = new URLSearchParams({
    bookingNumber,
  })

  return `${paymentBaseUrl}?${params.toString()}`
}

export function buildGroupPaymentUrl(
  siteName: SiteName,
  bookingNumber: string,
  sourcePaymentUrl: string,
): string | undefined {
  const sourceUrl = parsePaymentUrl(sourcePaymentUrl)
  if (!sourceUrl) {
    return undefined
  }

  const variant = getGroupPaymentVariant(sourceUrl)
  if (!variant) {
    return undefined
  }

  const paymentBaseUrl = GROUP_PAYMENT_URLS[siteName]?.[variant]
  if (!paymentBaseUrl) {
    return undefined
  }

  const params = new URLSearchParams({
    bookingNumber,
  })
  const groupUserId = sourceUrl.searchParams.get('groupUserId')
  if (groupUserId) {
    params.set('groupUserId', groupUserId)
  }

  return `${paymentBaseUrl}?${params.toString()}`
}
